// Pure RBAC/menu trim for the zoned sidebar nav, kept out of the component so it can be tested
// without a DOM (see vitest.config.mjs — this project deliberately has no jsdom).
//
// The rule that matters: a zone whose items are ALL hidden must drop out entirely, header
// included. The header is the only thing naming the zone, so leaving it behind renders a
// "SCHEDULING" label sitting over nothing.

/**
 * Trim nav zones down to what this session may see.
 *
 * @param {Array<{items: Array<{module?: string, menuKey?: string, desktopOnly?: boolean}>}>} zones — NAV_ZONES
 * @param {object} ctx
 * @param {boolean} ctx.isLoggedIn — gates are only applied to a real session; the logged-out
 *   shell shows every item disabled rather than hiding an unknowable subset.
 * @param {(module: string) => boolean} ctx.canView — hasPermission(module, "view")
 * @param {(menuKey: string) => boolean} ctx.isHidden — isMenuItemHidden(menuKey)
 * @param {boolean} [ctx.isDesktopApp] — !isWebMode(); a `desktopOnly` item drives Electron-only
 *   surfaces (e.g. the My Notes window) so it must not exist in web mode, logged in or not —
 *   unlike the RBAC gates this one applies to the logged-out shell too.
 * @returns {Array<{zone: object, items: Array<object>}>} zones with at least one visible item
 */
export function resolveVisibleZones(zones, { isLoggedIn, canView, isHidden, isDesktopApp }) {
  return (Array.isArray(zones) ? zones : [])
    .map((zone) => ({
      zone,
      // Hide (not disable) modules below view — matches the old Chats behavior.
      items: (zone?.items || []).filter((item) => {
        if (item.desktopOnly && !isDesktopApp) return false;
        if (item.module && isLoggedIn && !canView(item.module)) return false;
        if (item.menuKey && isLoggedIn && isHidden(item.menuKey)) return false;
        return true;
      }),
    }))
    .filter(({ items }) => items.length > 0);
}
